import Env from './env';
import LocalStorage from './local-storage';
import Logger from './logger';


export default class LogsManagement {

  #rmp;
  #runLogsFn = null;
  #runLogsOnSrcChangedFn = null;
  #logRetryTimeout = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  static getCheckSum(playerVersion) {
    const version = playerVersion.split('.');
    if (version.length === 3) {
      const sumVersion = parseInt(version[0]) + parseInt(version[1]) + parseInt(version[2]);
      const primes = [89, 251, 307, 523, 907, 1171, 1423];
      const randomIndex = Math.floor(Math.random() * primes.length);
      if (typeof primes[randomIndex] === 'number') {
        const randomPrime = primes[randomIndex];
        return sumVersion * randomPrime;
      }
    }
    return 0;
  }

  static hasFormData() {
    if (typeof FormData !== 'undefined') {
      try {
        const formData = new FormData();
        formData.append('test', 'test');
        return true;
      } catch (warning) {
        console.warn(warning);
        return false;
      }
    }
    return false;
  }

  static sendLog(formData) {
    return new Promise((resolve, reject) => {
      if (typeof XMLHttpRequest !== 'undefined') {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', 'https://www.rmp-data.com/ls/increment.php', true);
        xhr.timeout = 10000;
        xhr.onloadend = () => {
          if (typeof xhr.status === 'number' && xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject();
          }
        };
        xhr.ontimeout = () => {
          reject();
        };
        xhr.send(formData);
      } else {
        reject();
      }
    });
  }

  #getRmpBackLog() {
    let rmpBacklog = 0;
    if (this.#rmp.rmpLicense.type === 'plus' && LocalStorage.available(this.#rmp.allowLocalStorage)) {
      try {
        if (localStorage.getItem('rmpBacklog') !== null) {
          let rmpBacklogLS = localStorage.getItem('rmpBacklog');
          if (rmpBacklogLS !== null) {
            rmpBacklogLS = JSON.parse(rmpBacklogLS);
            if (rmpBacklogLS.value) {
              rmpBacklog = parseInt(rmpBacklogLS.value);
            }
          }
        }
      } catch (warning) {
        console.warn(warning);
      }
    }
    return rmpBacklog;
  }

  #appendFormDataAndSendLog() {
    // if offline write backlog
    if (!Env.isOnline) {
      if (LocalStorage.available(this.#rmp.allowLocalStorage)) {
        // read backlog
        const rmpBacklog = this.#getRmpBackLog();
        // add to backlog queue
        try {
          localStorage.setItem('rmpBacklog', JSON.stringify({ value: (rmpBacklog + 1).toString() }));
        } catch (warning) {
          console.warn(warning);
        }
      }
      return;
    }
    if (LogsManagement.hasFormData()) {
      const formData = new FormData();
      formData.append('licType', this.#rmp.rmpLicense.type);
      formData.append('licenseKey', this.#rmp.licenseKey);
      if (Env.protocol === 'file:' || Env.protocol === 'ms-appx-web:' || typeof window.Capacitor !== 'undefined' || typeof window.cordova !== 'undefined') {
        let appName = 'app';
        if (this.#rmp.appName) {
          appName = appName + '.' + ((this.#rmp.appName.replace(/\s/g, '-')).toLowerCase());
        }
        formData.append('hostname', appName);
      } else {
        if (this.#rmp.rmpLicense.hostname) {
          formData.append('hostname', this.#rmp.rmpLicense.hostname);
        } else {
          formData.append('hostname', 'unknown');
        }
      }
      formData.append('version', this.#rmp.playerVersion);
      formData.append('cs', LogsManagement.getCheckSum(this.#rmp.playerVersion));
      LogsManagement.sendLog(formData).then(() => {
        if (DEBUG) {
          if (this.#rmp.debugLevel === 777) {
            Logger.print('debug-more', 'logs sent');
          }
        }
      }).catch(() => {
        if (DEBUG) {
          if (this.#rmp.debugLevel === 777) {
            Logger.print('debug-more', 'logs failed to send - retry');
          }
        }
        window.clearTimeout(this.#logRetryTimeout);
        this.#logRetryTimeout = window.setTimeout(() => {
          LogsManagement.sendLog(formData).then(() => {
            if (DEBUG) {
              if (this.#rmp.debugLevel === 777) {
                Logger.print('debug-more', 'logs sent on second try');
              }
            }
          }).catch(() => {
            if (DEBUG) {
              if (this.#rmp.debugLevel === 777) {
                Logger.print('debug-more', 'logs failed to send twice');
              }
            }
          });
        }, 10000);
      });
    }
  }

  #runLogsOnSrcChanged() {
    this.#rmp.off('srcchanged', this.#runLogsOnSrcChangedFn);
    this.#rmp.on('playing adloaded', this.#runLogsFn);
  }

  #runLogs() {
    this.destroy();
    this.#rmp.on('srcchanged', this.#runLogsOnSrcChangedFn);
    this.#appendFormDataAndSendLog();
  }

  destroy() {
    this.#rmp.off('srcchanged', this.#runLogsOnSrcChangedFn);
    this.#rmp.off('playing adloaded', this.#runLogsFn);
    window.clearTimeout(this.#logRetryTimeout);
  }

  init() {
    const random = Math.floor((Math.random() * 10) + 1);
    if ((this.#rmp.rmpLicense.type === 'plus' && (random === 4 || random === 5)) || random === 5) {
      // wait for player start to send
      this.#runLogsFn = this.#runLogs.bind(this);
      this.#runLogsOnSrcChangedFn = this.#runLogsOnSrcChanged.bind(this);
      this.#rmp.on('playing adloaded', this.#runLogsFn);
      // read backlog
      const rmpBacklog = this.#getRmpBackLog();
      // if PLATFORM edition and backlog is present
      if (this.#rmp.rmpLicense.type === 'plus' && rmpBacklog > 0 && Env.isOnline) {
        // send backlog
        for (let i = 0; i < rmpBacklog; i++) {
          this.#appendFormDataAndSendLog();
        }
        // reset backlog
        if (LocalStorage.available(this.#rmp.allowLocalStorage)) {
          try {
            localStorage.setItem('rmpBacklog', JSON.stringify({ value: '0' }));
          } catch (warning) {
            console.warn(warning);
          }
        }
      }
    }
  }

}
